import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Wallet,
  LineChart,
  Bell,
  Sparkles,
  ShieldCheck,
  RefreshCw,
  Building2,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import StoreBadges from "@/components/brand/StoreBadges";
import { STS_LOGO } from "@/components/brand/Logo";
import Seo from "@/components/seo/Seo";
import TrialBanner from "@/components/sts/TrialBanner";
import PlanComparison from "@/components/sts/PlanComparison";
import PlanCards from "@/components/sts/PlanCards";
import IncludedFeatures from "@/components/sts/IncludedFeatures";
import { useLocalePath } from "@/i18n/LanguageSync";

const SHOT_BASE = "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/";
const SHOTS = [
  { src: `${SHOT_BASE}o7smugbl_Screenshot%201.webp`, testid: "sts-shot-home" },
  { src: `${SHOT_BASE}reg5tyj6_Screenshot%202.webp`, testid: "sts-shot-accounts" },
  { src: `${SHOT_BASE}n0fazz8x_Screenshot%203.webp`, testid: "sts-shot-goals" },
  { src: `${SHOT_BASE}yppiivxk_Screenshot%204.webp`, testid: "sts-shot-insights" },
];

export default function SafeToSpend() {
  const { t } = useTranslation("products");
  const lp = useLocalePath();
  const FEATURES = [
    { icon: Wallet, title: t("sts.features.safeToSpendTitle"), body: t("sts.features.safeToSpendBody") },
    { icon: Building2, title: t("sts.features.connectionsTitle"), body: t("sts.features.connectionsBody") },
    { icon: LineChart, title: t("sts.features.billsTitle"), body: t("sts.features.billsBody") },
    { icon: RefreshCw, title: t("sts.features.goalsTitle"), body: t("sts.features.goalsBody") },
    { icon: Sparkles, title: t("sts.features.insightsTitle"), body: t("sts.features.insightsBody") },
    { icon: Bell, title: t("sts.features.controlsTitle"), body: t("sts.features.controlsBody") },
  ];
  return (
    <>
      <Seo
        title={t("sts.seoTitle")}
        description={t("sts.seoDescription")}
        path="/safe-to-spend"
        preloadImages={[
          "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/o7smugbl_Screenshot%201.webp",
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            "name": "Safe to Spend 365",
            "operatingSystem": "iOS, Android",
            "applicationCategory": "FinanceApplication",
            "url": "https://atlasforgefinancial.com/safe-to-spend",
            "downloadUrl": [
              "https://apps.apple.com/us/app/safe-to-spend-365/id6792137232",
              "https://play.google.com/store/apps/details?id=com.atlasforge.safetospend",
            ],
            "installUrl": "https://apps.apple.com/us/app/safe-to-spend-365/id6792137232",
            "publisher": { "@type": "Organization", "name": "AtlasForge Financial" },
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Safe to Spend 365 Premium",
            "description":
              "Safe to Spend 365 Premium and Family subscriptions — free forever plan available, 7-day free trial on Premium, cancel anytime.",
            "brand": { "@type": "Brand", "name": "Safe to Spend 365" },
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "USD",
              "lowPrice": "0",
              "highPrice": "9.99",
              "offerCount": 5,
              "availability": "https://schema.org/InStock",
            },
          },
        ]}
      />
      <TrialBanner />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 af-mint-glow pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-24 md:pt-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <img
                src={STS_LOGO}
                alt="Safe to Spend 365"
                data-testid="sts-product-logo"
                width="480"
                height="480"
                decoding="async"
                fetchPriority="high"
                className="h-52 md:h-64 lg:h-72 w-auto object-contain mb-6"
              />
              <span
                data-testid="sts-eyebrow"
                className="inline-flex items-center gap-2 rounded-full border border-[#00C48C]/30 bg-[#E6FAF4] px-3 py-1.5 text-[12px] font-semibold text-[#00A373]"
              >
                <span className="h-2 w-2 rounded-full bg-[#00C48C] af-pulse-dot" />
                {t("sts.eyebrow")}
              </span>
              <h1
                data-testid="sts-hero-title"
                className="font-display mt-6 text-[44px] sm:text-[56px] lg:text-[68px] leading-[1.02] text-[#0A1128]"
              >
                {t("sts.heroTitle")}
              </h1>
              <p className="mt-6 text-[17px] leading-relaxed text-slate-600 max-w-xl">
                {t("sts.heroBody")}
              </p>

              <div className="mt-9">
                <StoreBadges product="sts-hero" />
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-[13px] text-slate-600">
                {t("sts.page.checks", { returnObjects: true }).map((c) => (
                  <span key={c} className="inline-flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#00C48C]" /> {c}
                  </span>
                ))}
              </div>
            </div>

            {/* Hero phone — real app screenshot */}
            <div className="lg:col-span-5 relative">
              <div className="mx-auto max-w-[340px]">
                <div
                  data-testid="sts-hero-phone"
                  className="relative rounded-[42px] bg-gradient-to-br from-[#0A1128] to-[#1E293B] p-1.5 shadow-2xl ring-1 ring-black/10"
                >
                  <img
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/o7smugbl_Screenshot%201.webp"
                    alt={t("sts.page.heroAlt")}
                    fetchPriority="high"
                    decoding="async"
                    width="340"
                    height="737"
                    className="rounded-[36px] w-full block"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("sts.page.how.eyebrow")}</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[44px] leading-[1.05] text-[#0A1128]">
              {t("sts.page.how.title")}
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {t("sts.page.how.steps", { returnObjects: true }).map((s, i) => (
              <div key={s.title} data-testid={`sts-how-step-${i + 1}`} className="rounded-2xl border border-slate-200 bg-white p-7 af-shadow-card">
                <p className="font-display text-[44px] leading-none text-[#00C48C]">{`0${i + 1}`}</p>
                <h3 className="font-display mt-4 text-[20px]">{s.title}</h3>
                <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="bg-[#F7F9FC] border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("sts.page.features.eyebrow")}</p>
              <h2 className="font-display mt-3 text-[36px] md:text-[44px] leading-[1.05] text-[#0A1128]">
                {t("sts.page.features.title")}
              </h2>
            </div>
            <StoreBadges product="sts-features" />
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl bg-white border border-slate-200 p-6 af-shadow-card">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#E6FAF4]">
                  <Icon className="h-5 w-5 text-[#00A373]" />
                </span>
                <h3 className="font-display mt-4 text-[17px]">{title}</h3>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* See it in action — real app screenshots */}
      <section id="screenshots" className="relative bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("sts.page.shots.eyebrow")}</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[44px] leading-[1.05] text-[#0A1128]">
              {t("sts.page.shots.title")}
            </h2>
            <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-2xl">
              {t("sts.page.shots.body")}
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {SHOTS.map((s, i) => ({ ...s, ...t("sts.page.shots.items", { returnObjects: true })[i] })).map((s) => (
              <figure
                key={s.testid}
                data-testid={s.testid}
                className="group"
              >
                <div className="relative rounded-[28px] bg-gradient-to-br from-[#0A1128] to-[#1E293B] p-1.5 shadow-2xl ring-1 ring-black/10 group-hover:-translate-y-1 transition-transform">
                  <img
                    src={s.src}
                    alt={`${t("sts.page.shots.altPrefix")}${s.title}`}
                    loading="lazy"
                    decoding="async"
                    className="rounded-[22px] w-full block"
                  />
                </div>
                <figcaption className="mt-4">
                  <p className="font-display text-[15px] text-[#0A1128]">{s.title}</p>
                  <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
                    {s.body}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Plans & pricing */}
      <section id="pricing" className="relative bg-[#F7F9FC] border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <PlanCards />
          <PlanComparison />
          <IncludedFeatures />
        </div>
      </section>

      {/* Security */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("sts.page.security.eyebrow")}</p>
              <h2 className="font-display mt-3 text-[34px] md:text-[42px] leading-[1.05] text-[#0A1128]">
                {t("sts.page.security.title")}
              </h2>
              <p className="mt-5 text-slate-600 max-w-lg">
                {t("sts.page.security.body")}
              </p>
              <Link
                to={lp("/legal/security")}
                data-testid="sts-link-security"
                className="mt-6 inline-flex items-center gap-2 text-[14px] font-medium text-[#0A1128] hover:gap-3 transition-all"
              >
                {t("sts.page.security.link")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-[#F7F9FC] p-8">
              {t("sts.page.security.items", { returnObjects: true }).map((it) => (
                <div key={it.title} className="py-4 border-b border-slate-200 last:border-0">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-[#00A373]" />
                    <p className="font-display text-[16px]">{it.title}</p>
                  </div>
                  <p className="text-[13px] text-slate-600 mt-1">{it.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1128] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28 text-center">
          <h2 data-testid="sts-cta-title" className="font-display text-[36px] md:text-[48px] leading-[1.05]">
            {t("sts.page.cta.title")}
          </h2>
          <p className="mt-5 text-slate-300 max-w-xl mx-auto">
            {t("sts.page.cta.body")}
          </p>
          <div className="mt-9 flex justify-center">
            <StoreBadges product="sts-bottom-cta" align="center" />
          </div>
        </div>
      </section>
    </>
  );
}
